import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';

import { CarouselPromo } from 'src/domains/layouts/webview/components/homePageCarouselPromos/carouselPromo/CarouselPromo';
import { QuickLinkTrayContainer } from 'src/domains/layouts/webview/components/quickLinkTray/QuickLinkTray';
import { NextRacesHomePage } from 'src/domains/sportsbook/webview/components/nextRaces/NextRacesHomePage';
import { EventsInPlayOrUpcoming } from 'src/domains/sportsbook/webview/components/containers/eventsInPlayOrUpcoming/EventsInPlayOrUpcoming';

import { HeroWidget } from 'src/domains/layouts/webview/components/heroEvents/heroWidget/HeroWidget';
import { PromotedEventsWidget } from 'src/domains/sportsbook/webview/components/containers/promotedEvents/PromotedEventsWidget';
import { EventsStartingSoon } from 'src/domains/sportsbook/webview/components/containers/eventsStartingSoon/EventsStartingSoon';
import { PriceChangeLegend } from 'src/domains/layouts/webview/components/priceChangeLegend/PriceChangeLegend';
import { SeoPagesContent } from 'src/domains/layouts/webview/components/seoPages/SeoPages';
import { TopEvents } from 'src/domains/sportsbook/webview/components/containers/topEvents/TopEvents';
import { VirtualGamesOnHomepage } from 'src/domains/casino/webview/gamesOnHomepage/virtuals/VirtualGamesHomepageLazyLoading';
import { CasinoGamesOnHomepage } from 'src/domains/casino/webview/gamesOnHomepage/casino/CasinoGamesHomepageLazyLoading';
import { HomepageWidgetType } from 'src/domains/common/configFeatures/ConfigFeatures';
import { AppCasinoState } from 'src/domains/casino/state/AppCasino.state';
import { XtremePushBanner } from '../../webview/components/xtremePush/xtremePushBanner/XtremePushBanner';
import { SliderDataState } from 'src/domains/sportsbook/webview/components/nextRaces/NextRaces.state';

type WidgetEntry = {
    component: React.ReactNode;
    condition?: boolean;
};

export const HomepageContent = observer('HomepageContent', () => {
    const common = useCommon();
    const appCasinoState = AppCasinoState.get(common);

    const config = ConfigComponents.get(common).config;
    const { hasGamesOnHomepage, hiddenSportsList, startingSoonEvents, topEvents, xtremePush } = config;
    const isRacingHiddenForOperator =
        hiddenSportsList.includes('horseracing') && hiddenSportsList.includes('greyhoundracing');
    const isFirstHRLoadingCompleted = SliderDataState.get(common, 'horseracing').isFirstHRLoadingCompleted;
    const widgetMap = new Map<HomepageWidgetType, WidgetEntry>([
        ['promoCarousel', { component: <CarouselPromo modeType='homepage' /> }],
        ['quickLinkTray', { component: <QuickLinkTrayContainer /> }],
        ['racingWidget', { component: <NextRacesHomePage />, condition: isRacingHiddenForOperator === false }],
        ['inPlay', { component: <EventsInPlayOrUpcoming showPriceLegend={false} /> }],
        ['heroWidget', { component: <HeroWidget /> }],
        ['xtremePush', { component: <XtremePushBanner type='sportsbook' />, condition: xtremePush === true }],
        ['racingWidget', { component: <NextRacesHomePage />, condition: isRacingHiddenForOperator === false }],
        [
            'topEvents',
            { component: <TopEvents />, condition: topEvents === true && isFirstHRLoadingCompleted === true },
        ],
        [
            'inPlay',
            {
                component: <EventsInPlayOrUpcoming showPriceLegend={false} />,
            },
        ],
        [
            'startingSoon',
            {
                component: <EventsStartingSoon showPriceLegend={false} />,
                condition: startingSoonEvents === true && isFirstHRLoadingCompleted === true,
            },
        ],
        [
            'casinoGames',
            {
                component: <CasinoGamesOnHomepage appCasinoState={appCasinoState} />,
                condition: hasGamesOnHomepage === true && isFirstHRLoadingCompleted === true,
            },
        ],
        [
            'promotedEvents',
            {
                component: <PromotedEventsWidget />,
                condition: isFirstHRLoadingCompleted === true && topEvents === false,
            },
        ],
        [
            'virtualGames',
            {
                component: <VirtualGamesOnHomepage appCasinoState={appCasinoState} />,
                condition: hasGamesOnHomepage === true && isFirstHRLoadingCompleted === true,
            },
        ],
        ['priceChangeLegend', { component: <PriceChangeLegend isRace={false} /> }],
        ['seoPagesContent', { component: <SeoPagesContent pageSlug='homepage' /> }],
    ]);

    const widgetsOrder = [...new Set(config.homepageWidgetsOrder)];

    return (
        <>
            {widgetsOrder.map((key) => {
                const widget = widgetMap.get(key);

                if (!widget || widget.condition === false) {
                    return null;
                }

                return <React.Fragment key={key}>{widget.component}</React.Fragment>;
            })}
        </>
    );
});
